import { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import PasswordInput from "../../components/input/PasswordInput/PasswordInput";
import { StateContext } from "../../contexts/state.context";
import { runChangePassword, runGetMe } from "../../utils/services";
import styles from "./Profile.module.scss";

const Profile = () => {
	const { currentSession } = useContext(StateContext);
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [currentPassword, setCurrentPassword] = useState("");
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState("");
	const [userData, setUserData] = useState(null);
	const [isSaving, setIsSaving] = useState(false);
	const navigate = useNavigate();

	const username = currentSession?.username || "Usuario";
	const firstName = userData?.firstName?.trim() || "";
	const lastName = userData?.lastName?.trim() || "";
	const fullName =
		[firstName, lastName].filter(Boolean).join(" ") || username;

	const translatePasswordError = (error) => {
		const backendMessage = error?.message || "";

		if (backendMessage.includes("longer than 8 characters")) {
			return "La nueva clave debe tener mas de 8 caracteres y combinar mayusculas, minusculas y un simbolo.";
		}

		if (backendMessage.includes("Invalid current password")) {
			return "Clave actual incorrecta.";
		}

		return "No se pudo guardar.";
	};

	useEffect(() => {
		let isMounted = true;

		const loadUserData = async () => {
			try {
				const response = await runGetMe();
				if (!isMounted) return;

				setUserData(response?.message?.user || response?.message || null);
			} catch {
				if (!isMounted) return;
				setUserData(null);
			}
		};

		loadUserData();

		return () => {
			isMounted = false;
		};
	}, []);

	const handleSubmit = async (event) => {
		event.preventDefault();
		setMessage("");

		if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
			setMessageType("error");
			setMessage("Complete los campos.");
			return;
		}

		if (newPassword !== confirmPassword) {
			setMessageType("error");
			setMessage("Las contraseñas no coinciden.");
			return;
		}

		setIsSaving(true);

		try {
			const response = await runChangePassword({
				currentPassword,
				newPassword,
			});

			if (response?.code === "LRG001") {
				setMessageType("success");
				setMessage("Cambio listo.");
				setCurrentPassword("");
				setNewPassword("");
				setConfirmPassword("");
				return;
			}

			setMessageType("error");
			setMessage("No se pudo guardar.");
		} catch (error) {
			if (error?.code === "LRG002") {
				setMessageType("error");
				setMessage(translatePasswordError(error));
				return;
			}

			if (error?.code === "LRL011") {
				setMessageType("error");
				setMessage("Clave actual incorrecta.");
				return;
			}

			setMessageType("error");
			setMessage("No se pudo guardar.");
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div className={styles.profilePage}>
			<div className={styles.profileCard}>
				<button
					type="button"
					className={styles.backButton}
					onClick={() => navigate("/licence")}
					aria-label="Volver al componente principal"
				>
					<span className={styles.backArrow}>←</span>
					<span>Volver</span>
				</button>

					<div className={styles.profileHeader}>
						<div className={styles.avatar}>{username.charAt(0).toUpperCase()}</div>
						<div>
							<p className={styles.kicker}>Perfil de usuario</p>
							<h1 className={styles.title}>{fullName}</h1>
						</div>
					</div>

				<div className={styles.infoGrid}>
					<div className={styles.infoItem}>
						<span className={styles.label}>Nombre</span>
						<span className={styles.value}>{firstName || "-"}</span>
					</div>
					<div className={styles.infoItem}>
						<span className={styles.label}>Apellido</span>
						<span className={styles.value}>{lastName || "-"}</span>
					</div>
					<div className={styles.infoItem}>
						<span className={styles.label}>Usuario</span>
						<span className={styles.value}>{userData?.username || username}</span>
					</div>
					<div className={styles.infoItem}>
						<span className={styles.label}>Rol</span>
						<span className={styles.value}>{userData?.role || "admin"}</span>
					</div>
					<div className={styles.infoItem}>
						<span className={styles.label}>Estado</span>
						<span className={styles.value}>
							{userData?.isActive === false ? "Inactivo" : "Activo"}
						</span>
					</div>
				</div>

				<form className={styles.form} onSubmit={handleSubmit}>
					<PasswordInput
						name="currentPassword"
						label="Contraseña actual"
						value={currentPassword}
						onChange={setCurrentPassword}
						autoComplete="current-password"
						className={styles.passwordInput}
					/>
					<PasswordInput
						name="newPassword"
						label="Nueva contraseña"
						value={newPassword}
						onChange={setNewPassword}
						autoComplete="new-password"
						className={styles.passwordInput}
					/>
					<PasswordInput
						name="confirmPassword"
						label="Confirmar contraseña"
						value={confirmPassword}
						onChange={setConfirmPassword}
						autoComplete="new-password"
						className={styles.passwordInput}
					/>

					<div
						className={`${styles.messageBox} ${
							message ? styles.messageBoxVisible : ""
						} ${
							messageType === "success"
								? styles.messageSuccess
								: styles.messageError
						}`}
						role="alert"
						aria-live="polite"
					>
						{message}
					</div>

					<div className={styles.actions}>
						<button
							type="button"
							className={styles.secondaryButton}
							onClick={() => navigate("/licence")}
						>
							Cancelar
						</button>
						<button type="submit" className={styles.primaryButton} disabled={isSaving}>
							{isSaving ? "Guardando..." : "Guardar cambio"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default Profile;
